import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  COMMAND_GROUPS,
  GROUP_ORDER_DEFAULT,
  GROUP_ORDER_SEARCH,
  enrichCommand,
  getAllCommands,
  getContextualCommands,
} from '../../../constants/navigation/commandRegistry';
import { ALWAYS_ALLOWED_PATH_PREFIXES } from '../../../constants/navigation/navConfig';

function isItemAccessible(item, accessibleSection) {
  if (!item.sectionname) {
    if (
      item.path &&
      ALWAYS_ALLOWED_PATH_PREFIXES.some(p => item.path === p || item.path.startsWith(`${p}/`))
    ) {
      return true;
    }
    return true;
  }

  if (accessibleSection?.length === 0) {
    return true;
  }

  return accessibleSection?.includes(item.sectionname);
}

function getMatchScore(item, query) {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return 0;

  const label = item.label.toLowerCase();
  const description = (item.description || '').toLowerCase();
  const keywords = (item.keywords || []).join(' ').toLowerCase();
  const group = (item.group || '').toLowerCase();
  const searchable = `${label} ${description} ${keywords} ${group}`;
  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);

  if (label.startsWith(normalizedQuery)) return 100;
  if (label.includes(normalizedQuery)) return 90;
  if (tokens.length > 1 && tokens.every(token => searchable.includes(token))) return 80;
  if (keywords.includes(normalizedQuery)) return 70;
  if (searchable.includes(normalizedQuery)) return 60;
  if (tokens.some(token => searchable.includes(token))) return 40;

  return -1;
}

function useCommandItems(query) {
  const location = useLocation();
  const { accessibleSection, isAccessibleSectionLoaded } = useSelector(state => state.team.data);
  const hasCreditAccess = useSelector(state => state.app.hasCreditAccess);
  const isSponsorsActive = useSelector(state => state.branding.branding.isSponsorsActive);
  const isDarkTheme = useSelector(state => state.app.isDarkTheme);
  const platform = useSelector(state => state.platform.platform);

  const normalizedQuery = query.trim();
  const isSearching = normalizedQuery.length > 0;

  const { groupedItems, flatItems } = useMemo(() => {
    if (!isAccessibleSectionLoaded) {
      return { groupedItems: {}, flatItems: [] };
    }

    const mergedCommands = [
      ...getAllCommands(platform),
      ...getContextualCommands(location.pathname, { isSponsorsActive, platform }),
    ];

    const seenIds = new Set();
    const allItems = mergedCommands
      .filter(item => {
        if (seenIds.has(item.id)) return false;
        seenIds.add(item.id);
        return true;
      })
      .map(item => enrichCommand(item, { isDarkTheme }));

    const filtered = allItems
      .filter(item => {
        if (item.requiresSponsorsActive && !isSponsorsActive) return false;
        if (item.requiresCreditAccess && !hasCreditAccess) return false;
        if (!isItemAccessible(item, accessibleSection)) return false;
        if (isSearching && getMatchScore(item, normalizedQuery) < 0) return false;
        return true;
      })
      .map(item => ({
        ...item,
        matchScore: isSearching ? getMatchScore(item, normalizedQuery) : 0,
      }));

    const groupOrder = isSearching ? GROUP_ORDER_SEARCH : GROUP_ORDER_DEFAULT;

    const grouped = filtered.reduce((acc, item) => {
      const group = item.group || COMMAND_GROUPS.NAVIGATION;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(item);
      return acc;
    }, {});

    groupOrder.forEach(group => {
      if (grouped[group]) {
        grouped[group].sort((a, b) => {
          if (isSearching && a.matchScore !== b.matchScore) {
            return b.matchScore - a.matchScore;
          }
          return a.label.localeCompare(b.label);
        });
      }
    });

    const ordered = {};
    groupOrder.forEach(group => {
      if (grouped[group]?.length) {
        ordered[group] = grouped[group];
      }
    });

    const flat = groupOrder.flatMap(group => ordered[group] || []);

    return { groupedItems: ordered, flatItems: flat };
  }, [
    accessibleSection,
    hasCreditAccess,
    isAccessibleSectionLoaded,
    isDarkTheme,
    isSearching,
    isSponsorsActive,
    location.pathname,
    normalizedQuery,
    platform,
  ]);

  return { groupedItems, flatItems, isSearching };
}

export default useCommandItems;
